import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Building2, Plus, Pencil, Trash2, Loader2, Mail, Phone } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = {
  name: "",
  contact_email: "",
  contact_phone: "",
  active: true,
  logo_url: "",
  persona: "",
};

// Brazilian phone mask: (XX) XXXX-XXXX or (XX) XXXXX-XXXX
function formatPhoneBR(value) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 11);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 3) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (len <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export default function SectorsPage() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/sectors");
      setSectors(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openNew() { setEditing(null); setForm(empty); setOpen(true); }
  function openEdit(s) {
    setEditing(s);
    setForm({
      name: s.name || "",
      contact_email: s.contact_email || "",
      contact_phone: formatPhoneBR(s.contact_phone || ""),
      active: !!s.active,
      logo_url: s.logo_url || "",
      persona: s.persona || "",
    });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sectors/${editing.id}`, form);
        toast.success("Setor atualizado");
      } else {
        await api.post("/sectors", form);
        toast.success("Setor criado");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        setForm((f) => ({ ...f, logo_url: reader.result }));
        setLogoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setLogoUploading(false);
    }
  }

  async function confirmDelete() {
    try {
      await api.delete(`/sectors/${deleteId}`);
      toast.success("Setor excluído");
      setDeleteId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  }

  return (
    <div>
      <PageHeader
        title="Setores"
        subtitle="Cada setor tem seu próprio chatbot, base de conhecimento e persona."
        action={
          <Button data-testid="new-sector-btn" onClick={openNew} className="bg-[#1e5a8e] hover:bg-[#164570]">
            <Plus className="w-4 h-4 mr-2" /> Novo setor
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : sectors.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Nenhum setor cadastrado"
          description="Crie seu primeiro setor para começar a treinar o chatbot com documentos específicos."
          action={<Button onClick={openNew} className="bg-[#1e5a8e] hover:bg-[#164570]"><Plus className="w-4 h-4 mr-2" />Criar primeiro setor</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sectors.map((s) => (
            <Card key={s.id} data-testid={`sector-card-${s.slug}`} className="p-6 border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#e6f0fa] flex items-center justify-center overflow-hidden shrink-0">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-6 h-6 text-[#1e5a8e]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-[Outfit] font-semibold text-slate-900 truncate">{s.name}</h3>
                    <Badge className={s.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}>
                      {s.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">/{s.slug}</p>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> {s.contact_email}</p>
                    {s.contact_phone && <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /> {s.contact_phone}</p>}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2 justify-end">
                <Button data-testid={`edit-sector-${s.slug}`} variant="outline" size="sm" onClick={() => openEdit(s)}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                </Button>
                <Button data-testid={`delete-sector-${s.slug}`} variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteId(s.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sector-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar setor" : "Novo setor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome do setor *</Label>
              <Input data-testid="sector-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Secretaria Acadêmica" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>E-mail de contato *</Label>
                <Input data-testid="sector-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="secretaria@ifpr.edu.br" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  data-testid="sector-phone"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: formatPhoneBR(e.target.value) })}
                  placeholder="(41) 3000-0000"
                  maxLength={15}
                  inputMode="tel"
                />
              </div>
            </div>
            <div>
              <Label>Logotipo do setor</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.logo_url && <img src={form.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />}
                <Input data-testid="sector-logo" type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading} />
              </div>
            </div>
            <div>
              <Label>Persona / comportamento do agente</Label>
              <Textarea
                data-testid="sector-persona"
                value={form.persona}
                onChange={(e) => setForm({ ...form, persona: e.target.value })}
                rows={4}
                placeholder="Ex: Você é o atendente virtual da Secretaria Acadêmica. Fale de forma cordial e formal, sempre em português. Priorize orientar sobre matrículas, certificados e trancamentos."
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">Setor ativo</p>
                <p className="text-xs text-slate-500">Se desativado, o chatbot público não responde.</p>
              </div>
              <Switch data-testid="sector-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-sector-btn" onClick={save} disabled={saving || !form.name || !form.contact_email} className="bg-[#1e5a8e] hover:bg-[#164570]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir setor?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os documentos e vetores desse setor serão excluídos. Coordenadores vinculados
              ficarão sem setor. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-sector" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
